import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ScanLine, Upload, Loader2, Trash2, Check, ImageIcon, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/config/api';

const WhiteboardScanModal = ({ isOpen, onClose, members, user, createTask }) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'scanning' | 'review'
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedTasks, setExtractedTasks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setStep('upload');
    setImageFile(null);
    setImagePreview(null);
    setExtractedTasks([]);
    onClose();
  };

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, []);

  const handleScan = async () => {
    if (!imageFile) return;
    setStep('scanning');
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const res = await axios.post(`${API}/tasks/scan-whiteboard`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExtractedTasks(res.data.tasks.map((t, i) => ({ ...t, _key: i, _selected: true })));
      setStep('review');
    } catch (err) {
      console.error(err);
      alert('Errore durante l\'analisi. Riprova.');
      setStep('upload');
    }
  };

  const updateTask = (key, field, value) => {
    setExtractedTasks(prev =>
      prev.map(t => t._key === key ? { ...t, [field]: value } : t)
    );
  };

  const toggleMemberInTask = (key, memberId, memberName) => {
    setExtractedTasks(prev => prev.map(t => {
      if (t._key !== key) return t;
      const ids = t.assigned_to_ids || [];
      const names = t.assigned_to_names || [];
      if (ids.includes(memberId)) {
        return {
          ...t,
          assigned_to_ids: ids.filter(id => id !== memberId),
          assigned_to_names: names.filter(n => n !== memberName),
        };
      }
      return {
        ...t,
        assigned_to_ids: [...ids, memberId],
        assigned_to_names: [...names, memberName],
      };
    }));
  };

  const removeTask = (key) => {
    setExtractedTasks(prev => prev.filter(t => t._key !== key));
  };

  const handleConfirmCreate = async () => {
    const toCreate = extractedTasks.filter(t => t._selected && t.title.trim());
    if (toCreate.length === 0) return;
    setCreating(true);
    for (const t of toCreate) {
      await createTask({
        title: t.title.trim(),
        assigned_to_ids: t.assigned_to_ids || [],
        assigned_by: user.id,
        assigned_by_name: user.name,
        due_date: t.due_date || null,
      });
    }
    setCreating(false);
    handleClose();
  };

  const availableMembers = members.filter(m => m.role !== 'admin');
  const selectedCount = extractedTasks.filter(t => t._selected && t.title.trim()).length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-white text-xl flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-amber-400" />
            Scansiona Lavagna
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Fotografa una lavagna o piano scritto a mano — l'AI estrarrà le attività automaticamente.
          </DialogDescription>
        </DialogHeader>

        {/* STEP: UPLOAD */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
                dragOver
                  ? 'border-amber-500/60 bg-amber-500/5'
                  : 'border-zinc-700 bg-zinc-800/30 hover:border-zinc-600 hover:bg-zinc-800/50'
              )}
            >
              {imagePreview ? (
                <div className="space-y-3">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded-lg object-contain"
                  />
                  <p className="text-xs text-zinc-400">{imageFile?.name}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <ImageIcon className="w-12 h-12 text-zinc-600 mx-auto" />
                  <div>
                    <p className="text-sm text-zinc-300 font-medium">Trascina una foto qui</p>
                    <p className="text-xs text-zinc-500 mt-1">oppure clicca per selezionare</p>
                  </div>
                  <p className="text-[10px] text-zinc-600">JPG, PNG, WEBP</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => handleFile(e.target.files[0])}
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-400"
                onClick={handleClose}
              >
                Annulla
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-black font-bold"
                onClick={handleScan}
                disabled={!imageFile}
              >
                <ScanLine className="w-4 h-4 mr-2" />
                ANALIZZA
              </Button>
            </div>
          </div>
        )}

        {/* STEP: SCANNING */}
        {step === 'scanning' && (
          <div className="py-12 text-center space-y-4">
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto" />
            <div>
              <p className="text-white font-semibold">Analisi in corso...</p>
              <p className="text-zinc-500 text-xs mt-1">L'AI sta leggendo la tua lavagna</p>
            </div>
          </div>
        )}

        {/* STEP: REVIEW */}
        {step === 'review' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                {extractedTasks.length === 0
                  ? 'Nessuna attività trovata nell\'immagine.'
                  : `${extractedTasks.length} attività trovate — modifica o rimuovi prima di creare`}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-500 text-xs"
                onClick={() => { setStep('upload'); setExtractedTasks([]); }}
              >
                ← Ricarica foto
              </Button>
            </div>

            {extractedTasks.length === 0 && (
              <div className="text-center py-6 text-zinc-600 text-sm italic">
                Prova con un'altra foto o aggiungi manualmente le attività.
              </div>
            )}

            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {extractedTasks.map(task => (
                <div
                  key={task._key}
                  className={cn(
                    'rounded-xl border p-3 space-y-2 transition-all',
                    task._selected
                      ? 'border-amber-500/30 bg-amber-500/5'
                      : 'border-zinc-800 bg-zinc-900/50 opacity-50'
                  )}
                >
                  <div className="flex items-start gap-2">
                    {/* Checkbox toggle */}
                    <button
                      onClick={() => updateTask(task._key, '_selected', !task._selected)}
                      className={cn(
                        'mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all',
                        task._selected
                          ? 'border-amber-500 bg-amber-500'
                          : 'border-zinc-600 bg-transparent'
                      )}
                    >
                      {task._selected && <Check className="w-2.5 h-2.5 text-black" />}
                    </button>

                    <div className="flex-1 space-y-2">
                      {/* Title */}
                      <Input
                        value={task.title}
                        onChange={e => updateTask(task._key, 'title', e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                        placeholder="Titolo attività..."
                      />

                      <div className="flex gap-2 items-center flex-wrap">
                        {/* Due date */}
                        <input
                          type="date"
                          value={task.due_date || ''}
                          onChange={e => updateTask(task._key, 'due_date', e.target.value || null)}
                          className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-md px-2 py-1 h-7"
                        />

                        {/* Member pills */}
                        <div className="flex gap-1 flex-wrap">
                          {availableMembers.map(m => (
                            <button
                              key={m.id}
                              onClick={() => toggleMemberInTask(task._key, m.id, m.name)}
                              className={cn(
                                'text-[10px] px-2 py-0.5 rounded-full border transition-all',
                                (task.assigned_to_ids || []).includes(m.id)
                                  ? 'border-amber-500/60 bg-amber-500/15 text-amber-300'
                                  : 'border-zinc-700 bg-zinc-800 text-zinc-500 hover:border-zinc-600'
                              )}
                            >
                              {m.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {task.notes && (
                        <p className="text-[10px] text-zinc-500 italic">{task.notes}</p>
                      )}
                    </div>

                    {/* Remove button */}
                    <button
                      onClick={() => removeTask(task._key)}
                      className="text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2 border-t border-zinc-800">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-400"
                onClick={handleClose}
              >
                Annulla
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-black font-bold"
                onClick={handleConfirmCreate}
                disabled={creating || selectedCount === 0}
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    CREA {selectedCount} {selectedCount === 1 ? 'ORDINE' : 'ORDINI'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhiteboardScanModal;
